// @flow
import { intersection, isEmpty, find, clone } from 'lodash';
import { Event } from 'types/event';
import type { AppState } from 'modules/rootReducer';
import type {
  ActiveEventFilters,
  ActiveProjectFilters,
  EventFilters,
} from 'types/filter';
import type { GalleryImageType } from 'types/common';

const getUpcomingEvents = (state: AppState): Array<Event> | null => {
  const upcomingEvents = state.event.upcoming;
  if (!upcomingEvents) {
    return [];
  }

  return upcomingEvents;
};

const getOtherEvents = (state: AppState, eventSlug) => {
  const events = state.event.upcoming;
  if (!events) {
    return [];
  }

  return events.filter(event => {
    return event.urlSlug !== eventSlug;
  });
};

const getUpcomingEventsLoading = (state: AppState): boolean => {
  return state.event.loading.upcoming;
};

const getUpcomingEventsError = (state: AppState): string | null => {
  return state.event.error.upcoming;
};

const getLatestShow = (state: AppState): Object<Event> | null => {
  return state.event.latest;
};

const getPastEvents = (state: AppState): Array<Event> | null => {
  const pastEvents = state.event.past;
  if (!pastEvents) {
    return [];
  }

  return pastEvents;
};

const getPastEventsLoading = (state: AppState): boolean => {
  return state.event.loading.past;
};

const getPastEventsError = (state: AppState): string | null => {
  return state.event.error.past;
};

const getPastEventFilters = (state: AppState): Object<EventFilters> | null => {
  return state.event.pastFilters;
};

const getUpcomingEventFilters = (
  state: AppState
): Object<EventFilters> | null => {
  return state.event.upcomingFilters;
};

const getArtistGenres = (state: AppState): Object<EventFilters> | null => {
  return state.event.artistsAndGenres;
};

const getActiveEventFilters = (
  state: AppState
): Object<ActiveProjectFilters> | null => {
  return state.event.activeFilters;
};

const getActiveUpcomingEventFilters = (
  state: AppState
): Object<ActiveProjectFilters> | null => {
  return state.event.activeUpcomingFilters;
};

const getGallery = (state: AppState): Array<GalleryImageType> | null => {
  return state.event.gallery;
};

const getGalleryLoading = (state: AppState): boolean => {
  return state.event.loading.gallery;
};

const getGalleryError = (state: AppState): string | null => {
  return state.event.error.gallery;
};

const getActiveEventFiltersNames = (state: AppState): Array<string> => {
  const filterNames = [];
  const eventFilters: EventFilters = getPastEventFilters(state);
  const activeEventFilters: ActiveEventFilters = getActiveEventFilters(state);
  if (!eventFilters || !activeEventFilters) {
    return filterNames;
  }

  if (activeEventFilters.venue.length > 0) {
    const venueNames = eventFilters.venue
      .filter(venue => {
        return activeEventFilters.venue.indexOf(venue.venueId) > -1;
      })
      .map(venue => venue.venue);
    filterNames.push(...venueNames);
  }

  if (activeEventFilters.genre.length > 0) {
    const genreNames = eventFilters.genre
      .filter(genre => {
        return activeEventFilters.genre.indexOf(genre.genreId) > -1;
      })
      .map(genre => genre.genre);
    filterNames.push(...genreNames);
  }

  if (activeEventFilters.city.length > 0) {
    const cityNames = eventFilters.city
      .filter(city => {
        return activeEventFilters.city.indexOf(city.cityId) > -1;
      })
      .map(city => city.city);
    filterNames.push(...cityNames);
  }

  if (activeEventFilters.artist.length > 0) {
    const artistNames = eventFilters.artist
      .filter(artist => {
        return activeEventFilters.artist.indexOf(artist.artistId) > -1;
      })
      .map(artist => artist.title);
    filterNames.push(...artistNames);
  }

  return filterNames;
};

const getActiveUpcomingEventFiltersNames = (state: AppState): Array<string> => {
  const filterNames = [];
  const eventFilters: EventFilters = getUpcomingEventFilters(state);
  const activeEventFilters: ActiveEventFilters = getActiveUpcomingEventFilters(
    state
  );
  if (!eventFilters || !activeEventFilters) {
    return filterNames;
  }

  if (activeEventFilters.venue.length > 0) {
    const venueNames = eventFilters.venue
      .filter(venue => {
        return activeEventFilters.venue.indexOf(venue.venueId) > -1;
      })
      .map(venue => venue.venue);
    filterNames.push(...venueNames);
  }

  if (activeEventFilters.genre.length > 0) {
    const genreNames = eventFilters.genre
      .filter(genre => {
        return activeEventFilters.genre.indexOf(genre.genreId) > -1;
      })
      .map(genre => genre.genre);
    filterNames.push(...genreNames);
  }

  if (activeEventFilters.city.length > 0) {
    const cityNames = eventFilters.city
      .filter(city => {
        return activeEventFilters.city.indexOf(city.cityId) > -1;
      })
      .map(city => city.city);
    filterNames.push(...cityNames);
  }

  if (activeEventFilters.artist.length > 0) {
    const artistNames = eventFilters.artist
      .filter(artist => {
        return activeEventFilters.artist.indexOf(artist.artistId) > -1;
      })
      .map(artist => artist.title);
    filterNames.push(...artistNames);
  }

  return filterNames;
};

// Filtering is now server-side — state.event.past already contains the correct results
const getFilteredEvents = (state: AppState) => {
  return state.event.past || [];
};

const getPastEventsPagination = (state: AppState) =>
  state.event.pastPagination || { page: 1, total: 0, totalPages: 1 };

const hasMorePastEvents = (state: AppState): boolean => {
  const { page, totalPages } = getPastEventsPagination(state);
  return page < totalPages;
};

const getPastEventsLoadingMore = (state: AppState): boolean =>
  state.event.loading.pastMore;

const getPastFiltersLoading = (state: AppState): boolean =>
  state.event.loading.pastFilters;

const getUpcomingFilteredEvents = (state: AppState) => {
  let events = state.event.overlayData;
  const activeFilters = getActiveUpcomingEventFilters(state);
  if (!activeFilters) {
    return events;
  }

  if (activeFilters.venue && activeFilters.venue.length > 0) {
    events = events.filter(event => {
      return activeFilters.venue.indexOf(event.id) > -1;
    });
  }

  if (activeFilters.genre && activeFilters.genre.length > 0) {
    events = events.filter(event => {
      const genreTypes = event.genreId.map(id => id);
      return intersection(activeFilters.genre, genreTypes).length > 0;
    });
  }

  if (activeFilters.city && activeFilters.city.length > 0) {
    events = events.filter(event => {
      return activeFilters.city.indexOf(event.cityId) > -1;
    });
  }

  if (activeFilters.artist && activeFilters.artist.length > 0) {
    events = events.filter(event => {
      const artists = event.artistId.map(id => id);
      return intersection(activeFilters.artist, artists).length > 0;
    });
  }

  return events;
};

const isEventsFiltered = (state: AppState) => {
  const activeFilters: ActiveEventFilters = getActiveEventFilters(state);
  if (!activeFilters) {
    return false;
  }

  return (
    activeFilters.venue.length > 0 ||
    activeFilters.genre.length > 0 ||
    activeFilters.city.length > 0 ||
    activeFilters.artist.length > 0
  );
};

const isUpcomingEventsFiltered = (state: AppState) => {
  const activeUpcomingFilters: ActiveEventFilters = getActiveUpcomingEventFilters(
    state
  );
  if (!activeUpcomingFilters) {
    return false;
  }
  return (
    activeUpcomingFilters.venue.length > 0 ||
    activeUpcomingFilters.genre.length > 0 ||
    activeUpcomingFilters.city.length > 0 ||
    activeUpcomingFilters.artist.length > 0
  );
};

const getUpcomingEventsOverlayData = (state: AppState) => {
  if (!state.event.overlayData || state.event.overlayData.length === 0) {
    return [];
  }
  return state.event.overlayData;
};

const getEventBannerLoading = (state: AppState) => {
  return state.event.loading.banner;
};

const getEventDetailBanner = (state: AppState) => {
  if (!state.event.banner) {
    return [];
  }

  return state.event.banner;
};

const getEventArtistsDetail = (state: AppState) => {
  if (!state.event.eventArtistDetails) {
    return [];
  }

  return state.event.eventArtistDetails;
};

const getStandingTickets = (state: AppState) => {
  if (!state.event.totalStandingTickets) return 0;
  return state.event.totalStandingTickets;
};

const getTableSelected = (state: AppState) => {
  if (!state.event.tableSelected) return 0;
  return state.event.tableSelected.tableData;
};

//The selector is used to get the tables-data for any given outlet_id
const getOutletEventSeating = (state: AppState): Array<Event> | null => {
  if (!state.event.outlet_seating) {
    return [];
  }
  return state.event.outlet_seating;
};

const getOutletEventSeatingLoading = (state: AppState): boolean => {
  return state.event.loading.outlet_seating;
};

const getOutletEventSeatingError = (state: AppState): string | null => {
  return state.event.error.outlet_seating;
};

//The selector is used to get the standing tickets for any given event at a particular time
const getEventStandingTickets = (state: AppState): Array<Event> | null => {
  if (!state.event.booked_standing_tickets) {
    return 0;
  }
  return state.event.booked_standing_tickets;
};

const getEventStandingTicketsLoading = (state: AppState): boolean => {
  return state.event.loading.booked_standing_tickets;
};

const getEventStandingTicketsError = (state: AppState): string | null => {
  return state.event.error.booked_standing_tickets;
};

//The selector is used to get the booked tables for any given event at a particular time
const getEventBookedTables = (state: AppState): Array<Event> | null => {
  // if no table is booked for given event at a particular time.
  if (!isEmpty(state.event.outlet_seating)) {
    iterateEventTables(state.event.outlet_seating, [], false);
  }
  let outlet_seating = clone(state.event.outlet_seating);
  let booked_tables = clone(state.event.event_booked_tables);
  if (isEmpty(booked_tables)) {
    if (outlet_seating) return outlet_seating;
    else return [];
  }
  // if there is one/more table(s) are booked then modify the initial table status for the particular tableId.
  if (!isEmpty(outlet_seating)) {
    iterateEventTables(outlet_seating, booked_tables, true);
    return outlet_seating;
  }
};

const iterateEventTables = (tables, bookedTables, callModifyTableStatus) => {
  Object.values(tables).map(row_data => {
    row_data.map(table => {
      table.booked = false;
      callModifyTableStatus && modifyTableStatus(table, bookedTables);
    });
  });
};

const modifyTableStatus = (table, booked_tables) => {
  find(booked_tables, function(booked_table) {
    if (booked_table.outlet_seating_id === table.id) table.booked = true;
  });
};

const getEventBookedTablesLoading = (state: AppState): boolean => {
  return state.event.loading.event_booked_tables;
};

const getEventBookedTablesError = (state: AppState): string | null => {
  return state.event.error.event_booked_tables;
};

//This selector is used to get the fnb packages for any given event at a particular time
const getEventFnBPackages = (state: AppState): Array<Event> | null => {
  if (!state.event.fnb_packages) {
    return [];
  }
  return state.event.fnb_packages;
};

const getEventFnBPackagesLoading = (state: AppState): boolean => {
  return state.event.loading.fnb_packages;
};

const getEventFnBPackagesError = (state: AppState): string | null => {
  return state.event.error.fnb_packages;
};

const EventDetailsIsLoading = state => {
  return (
    getUpcomingEventsLoading(state) &&
    getEventBannerLoading(state) &&
    getOutletEventSeatingLoading(state) &&
    getEventStandingTicketsLoading(state) &&
    getEventBookedTablesLoading(state)
  );
};

export const EventSelector = {
  getUpcomingEvents,
  getUpcomingEventsLoading: getUpcomingEventsLoading,
  getUpcomingEventsError: getUpcomingEventsError,
  getGallery,
  getGalleryLoading,
  getGalleryError,
  getLatestShow,
  getPastEvents,
  getPastEventsLoading,
  getPastEventsError,
  getUpcomingEventFilters,
  getPastEventFilters,
  getActiveEventFilters,
  getActiveEventFiltersNames,
  getFilteredEvents,
  isEventsFiltered,
  getArtistGenres,
  getUpcomingEventsOverlayData,
  getActiveUpcomingEventFilters,
  isUpcomingEventsFiltered,
  getActiveUpcomingEventFiltersNames,
  getUpcomingFilteredEvents,
  getEventBannerLoading,
  getEventDetailBanner,
  getEventArtistsDetail,
  getOtherEvents,
  getStandingTickets,
  getTableSelected,
  getOutletEventSeating,
  getOutletEventSeatingLoading,
  getOutletEventSeatingError,
  getEventStandingTickets,
  getEventStandingTicketsLoading,
  getEventStandingTicketsError,
  getEventBookedTables,
  getEventBookedTablesLoading,
  getEventBookedTablesError,
  getEventFnBPackages,
  getEventFnBPackagesLoading,
  getEventFnBPackagesError,
  EventDetailsIsLoading,
  getPastEventsPagination,
  hasMorePastEvents,
  getPastEventsLoadingMore,
  getPastFiltersLoading,
};
